export const introductionToPoliticalScienceCourse = {
  id: "introduction-to-political-science-2024",
  title: "Introduction to Political Science: Government, Power & Politics",
  shortDescription: "Examine political systems, governmental institutions, and the dynamics of power in domestic and international contexts.",
  description: "Comprehensive introduction to political science covering political theory, comparative government, public policy, and international relations. Students will analyze different political systems, understand the role of institutions, and examine contemporary political issues and challenges.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from political science professors and policy analysts",
  thumbnailUrl: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand major political theories and ideologies",
    "Analyze different forms of government and political systems",
    "Examine the role of political institutions and processes",
    "Evaluate public policy formation and implementation",
    "Understand voting behavior and political participation",
    "Analyze international relations and global politics",
    "Examine the relationship between politics and economics",
    "Evaluate contemporary political challenges and debates"
  ],

  prerequisites: [
    "High school education",
    "Basic understanding of current events",
    "Interest in government and politics"
  ],

  skills: [
    "Political Analysis",
    "Critical Thinking",
    "Policy Evaluation",
    "Research Methods",
    "Debate and Argumentation",
    "Statistical Analysis",
    "Communication Skills",
    "Comparative Analysis"
  ],

  marketData: {
    averageSalary: 55000,
    salaryRange: {
      min: 38000,
      max: 80000
    },
    jobDemand: 3.6,
    marketTrend: "stable",
    careerPaths: [
      "Policy Analyst",
      "Government Relations Specialist",
      "Campaign Manager",
      "Diplomatic Service",
      "Political Journalist",
      "Public Administrator"
    ],
    relatedSkills: [
      "Policy Analysis",
      "Research",
      "Communication",
      "Data Analysis",
      "Public Speaking",
      "Writing"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "political-theory",
      title: "Foundations of Political Theory",
      description: "Core concepts of power, authority, legitimacy, and major political ideologies",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "power-authority",
          title: "Power and Authority",
          description: "Understanding the fundamental concepts of political power and legitimate authority",
          type: "video",
          duration: 30
        },
        {
          id: "political-ideologies",
          title: "Major Political Ideologies",
          description: "Examination of liberalism, conservatism, socialism, and other political thought systems",
          type: "text",
          duration: 45,
          textContent: `# Major Political Ideologies

## Understanding Political Belief Systems

### Defining Political Ideology

Political ideology is a coherent set of beliefs about politics, power, and the role of government that provides a framework for understanding political issues and guiding political action.

**Components of Political Ideology:**
- **Core Values**: Fundamental beliefs about human nature and society
- **Role of Government**: Views on the proper scope and function of government
- **Economic Philosophy**: Beliefs about markets, property, and economic equality
- **Social Issues**: Positions on individual rights, family, and cultural values
- **Political Process**: Views on democracy, representation, and political participation

### Classical Liberalism

**Core Principles**
- **Individual Liberty**: Maximum personal freedom
- **Limited Government**: Minimal state intervention
- **Natural Rights**: Life, liberty, property
- **Rule of Law**: Government bound by legal constraints

**Key Thinkers**
- **John Locke**: Natural rights, social contract theory
- **Adam Smith**: Free market economics, invisible hand
- **John Stuart Mill**: Harm principle, individual autonomy

**Economic Views**
- **Free Market Capitalism**: Minimal economic regulation
- **Private Property**: Individual ownership rights
- **Laissez-faire**: Government non-interference in economy

**Contemporary Applications**
- **Libertarianism**: Modern extreme liberalism
- **Classical Liberal Parties**: Emphasis on individual rights
- **Free Trade**: International economic liberalism

### Modern Liberalism

**Evolution from Classical Liberalism**
- **Social Reform**: Government role in addressing inequality
- **Positive Rights**: Rights to education, healthcare, welfare
- **Regulated Capitalism**: Market economy with government oversight

**Key Principles**
- **Social Justice**: Equality of opportunity and outcome
- **Government Intervention**: Active state role in economy and society
- **Civil Rights**: Protection of minority rights
- **Internationalism**: Global cooperation and institutions

**Policy Positions**
- **Welfare State**: Social safety net programs
- **Progressive Taxation**: Higher taxes on wealthy
- **Environmental Protection**: Government regulation for sustainability
- **Multiculturalism**: Celebration of diversity

### Conservatism

**Traditional Conservatism**
- **Tradition**: Respect for established institutions and practices
- **Gradual Change**: Reform rather than revolution
- **Social Hierarchy**: Natural inequality and social order
- **Religious Values**: Importance of faith and moral guidance

**Key Thinkers**
- **Edmund Burke**: Critique of French Revolution, organic society
- **Russell Kirk**: Six canons of conservative thought
- **Michael Oakeshott**: Skepticism of abstract political schemes

**Modern Conservative Variants**

**Fiscal Conservatism**
- **Limited Government Spending**: Balanced budgets
- **Lower Taxes**: Especially on business and investment
- **Free Market**: Minimal economic regulation
- **Individual Responsibility**: Personal accountability

**Social Conservatism**
- **Traditional Family Values**: Marriage, family structure
- **Religious Freedom**: Role of faith in public life
- **Cultural Preservation**: Maintaining traditional culture
- **Law and Order**: Strong criminal justice system

**Neoconservatism**
- **Strong Defense**: Robust military and foreign policy
- **Democratic Promotion**: Spreading democracy globally
- **American Leadership**: Active international role

### Socialism

**Core Principles**
- **Economic Equality**: Reduction of wealth disparities
- **Social Ownership**: Public or worker control of production
- **Solidarity**: Collective responsibility and cooperation
- **Democratic Participation**: Worker and citizen involvement in decisions

**Types of Socialism**

**Democratic Socialism**
- **Parliamentary Democracy**: Electoral politics
- **Mixed Economy**: Public and private sectors
- **Gradual Reform**: Peaceful transition to socialism
- **Examples**: Scandinavian social democracy

**Marxist Socialism**
- **Class Struggle**: Conflict between workers and capitalists
- **Revolutionary Change**: Overthrow of capitalist system
- **Dictatorship of Proletariat**: Worker control of state
- **Eventual Communism**: Stateless, classless society

**Market Socialism**
- **Worker Cooperatives**: Employee-owned businesses
- **Market Mechanisms**: Price signals and competition
- **Social Ownership**: Public ownership of major industries

### Anarchism

**Core Beliefs**
- **No Government**: Abolition of state authority
- **Voluntary Association**: Free cooperation without coercion
- **Direct Action**: Grassroots political activism
- **Mutual Aid**: Community support and solidarity

**Types of Anarchism**
- **Individualist Anarchism**: Maximum individual freedom
- **Collectivist Anarchism**: Voluntary collective ownership
- **Anarcho-Syndicalism**: Worker control through unions

### Fascism

**Characteristics**
- **Ultranationalism**: Extreme patriotism and national superiority
- **Authoritarianism**: Dictatorial power and suppression of opposition
- **Militarism**: Glorification of violence and warfare
- **Mass Mobilization**: Popular support through propaganda

**Historical Examples**
- **Nazi Germany**: Racial ideology and totalitarian control
- **Fascist Italy**: Corporate state and imperial expansion
- **Distinguishing Features**: Neither left nor right economically

### Environmentalism/Green Politics

**Core Values**
- **Ecological Sustainability**: Living within planetary limits
- **Intergenerational Justice**: Responsibility to future generations
- **Holistic Thinking**: Interconnectedness of social and environmental issues
- **Grassroots Democracy**: Participatory decision-making

**Policy Positions**
- **Climate Action**: Aggressive measures to address climate change
- **Renewable Energy**: Transition from fossil fuels
- **Conservation**: Protection of natural habitats and biodiversity
- **Sustainable Development**: Economic growth within ecological limits

### Feminism

**Liberal Feminism**
- **Equal Rights**: Legal and political equality
- **Individual Choice**: Women's autonomy and opportunities
- **Reform**: Working within existing system

**Radical Feminism**
- **Patriarchy**: Systematic male domination
- **Structural Change**: Fundamental transformation of society
- **Women-Centered**: Focus on women's experiences and perspectives

**Intersectional Feminism**
- **Multiple Identities**: Race, class, gender, sexuality intersections
- **Inclusive Movement**: Recognition of diverse women's experiences

### Populism

**Characteristics**
- **People vs. Elite**: Division between ordinary people and corrupt establishment
- **Direct Democracy**: Unmediated popular will
- **Anti-Establishment**: Opposition to traditional political institutions
- **Charismatic Leadership**: Strong connection between leader and followers

**Left-Wing Populism**
- **Economic Inequality**: Focus on class divisions
- **Corporate Power**: Opposition to big business influence

**Right-Wing Populism**
- **Cultural Identity**: Emphasis on national or ethnic identity
- **Immigration**: Concern about cultural change and economic competition

### Comparing Ideologies

**Spectrum Analysis**
- **Left-Right Continuum**: Economic and social policy dimensions
- **Authority-Liberty Axis**: Role of government in individual lives
- **Traditionalism-Progressivism**: Attitude toward change

**Key Dimensions**
- **Economic**: Role of markets vs. government in economy
- **Social**: Individual rights vs. community values
- **Political**: Direct democracy vs. representative institutions
- **International**: Nationalism vs. internationalism

Understanding political ideologies helps explain why people hold different views on political issues and provides a framework for analyzing political debates and policy choices in democratic societies.`
        },
        {
          id: "ideology-mapping",
          title: "Political Ideology Assessment",
          description: "Interactive exercise to map political beliefs and understand ideological positions",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "ideology-assessment",
            instructions: "Analyze different policy positions and identify which political ideologies they represent. Consider both economic and social dimensions.",
            scenarios: [
              {
                id: "healthcare-policy",
                issue: "Healthcare Policy",
                positions: [
                  { text: "Universal healthcare funded by taxes", ideology: "Socialist/Social Democrat" },
                  { text: "Free market healthcare with minimal regulation", ideology: "Classical Liberal/Libertarian" },
                  { text: "Mixed public-private system with government regulation", ideology: "Modern Liberal" },
                  { text: "Healthcare through traditional institutions and charity", ideology: "Conservative" }
                ]
              },
              {
                id: "economic-policy",
                issue: "Economic Inequality",
                positions: [
                  { text: "Progressive taxation and wealth redistribution", ideology: "Socialist/Social Democrat" },
                  { text: "Flat tax and minimal government intervention", ideology: "Classical Liberal" },
                  { text: "Moderate progressive taxes with social programs", ideology: "Modern Liberal" },
                  { text: "Lower taxes with emphasis on private charity", ideology: "Conservative" }
                ]
              }
            ],
            hints: [
              "Consider the role of government in each position",
              "Think about underlying values about equality and freedom",
              "Look at attitudes toward change vs. tradition"
            ]
          }
        }
      ],
      quiz: {
        id: "political-theory-quiz",
        title: "Political Theory Quiz",
        description: "Test your understanding of political concepts and ideologies",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which political ideology emphasizes individual liberty and limited government?",
            options: [
              "Socialism",
              "Classical Liberalism",
              "Conservatism",
              "Fascism"
            ],
            correctAnswer: 1,
            explanation: "Classical liberalism emphasizes individual liberty, natural rights, and limited government intervention in personal and economic affairs."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the key difference between authority and power?",
            options: [
              "Authority is legitimate, power may not be",
              "Power is legal, authority is not",
              "Authority is temporary, power is permanent",
              "Power is democratic, authority is not"
            ],
            correctAnswer: 0,
            explanation: "Authority refers to legitimate power that is accepted as rightful by those subject to it, while power can exist without legitimacy."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Which ideology advocates for worker control of the means of production?",
            options: [
              "Liberalism",
              "Conservatism",
              "Socialism",
              "Libertarianism"
            ],
            correctAnswer: 2,
            explanation: "Socialism advocates for social or worker ownership and control of the means of production, rather than private ownership."
          }
        ]
      }
    },

    {
      id: "comparative-government",
      title: "Comparative Government Systems",
      description: "Analysis of different governmental structures and political systems around the world",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "democratic-systems",
          title: "Democratic Government Systems",
          description: "Parliamentary, presidential, and semi-presidential systems",
          type: "video",
          duration: 35
        },
        {
          id: "institutions-processes",
          title: "Political Institutions and Processes",
          description: "How different institutional arrangements affect political outcomes",
          type: "text",
          duration: 50,
          textContent: `# Political Institutions and Processes

## How Government Structure Affects Political Outcomes

### Constitutional Frameworks

**Written vs. Unwritten Constitutions**

**Written Constitutions**
- **Document-Based**: Single written document or set of documents
- **Supreme Law**: Constitution takes precedence over other laws
- **Amendment Process**: Formal procedures for constitutional change
- **Examples**: United States, Germany, India

**Unwritten Constitutions**
- **Multiple Sources**: Statutes, conventions, court decisions, customs
- **Parliamentary Sovereignty**: Legislature can change constitutional rules
- **Evolutionary Change**: Gradual adaptation over time
- **Examples**: United Kingdom, New Zealand

**Constitutional Features**
- **Bill of Rights**: Protected fundamental freedoms
- **Separation of Powers**: Division of government functions
- **Federalism**: Distribution of power between levels of government
- **Judicial Review**: Courts' power to review laws' constitutionality

### Executive Systems

**Presidential Systems**

**Characteristics**
- **Separation of Powers**: Executive independent from legislature
- **Direct Election**: President elected by popular vote (usually)
- **Fixed Term**: President serves predetermined period
- **Dual Executive**: President as both head of state and government

**Advantages**
- **Accountability**: Clear responsibility for executive actions
- **Stability**: Fixed terms provide predictable governance
- **Popular Mandate**: Direct connection to voters
- **Checks and Balances**: Legislative oversight of executive

**Disadvantages**
- **Gridlock**: Conflict between executive and legislative branches
- **Winner-Take-All**: Minority exclusion from executive power
- **Personalization**: Politics focused on individual leaders
- **Rigidity**: Difficult to remove ineffective leaders

**Examples**: United States, Brazil, Mexico, Philippines

**Parliamentary Systems**

**Characteristics**
- **Fusion of Powers**: Executive emerges from and accountable to legislature
- **Indirect Election**: Prime Minister chosen by parliament
- **Flexible Terms**: Government can be dismissed by vote of no confidence
- **Collective Responsibility**: Cabinet responsibility to parliament

**Advantages**
- **Responsiveness**: Government accountable to legislative majority
- **Efficiency**: Unified party control enables policy implementation
- **Flexibility**: Governments can be changed without elections
- **Representation**: Executive reflects legislative composition

**Disadvantages**
- **Instability**: Governments may fall frequently
- **Weak Opposition**: Majority dominance in parliament
- **Party Discipline**: Individual legislators have limited independence
- **Unclear Accountability**: Collective responsibility can obscure individual blame

**Examples**: United Kingdom, Canada, India, Germany

**Semi-Presidential Systems**

**Characteristics**
- **Dual Executive**: Both president and prime minister
- **Divided Responsibilities**: President (foreign policy, defense) and PM (domestic policy)
- **Cohabitation**: Possible when president and PM from different parties

**Examples**: France, Russia, Poland

### Legislative Systems

**Unicameral vs. Bicameral Legislatures**

**Unicameral (Single Chamber)**
- **Advantages**: Efficient decision-making, clear responsibility
- **Disadvantages**: Limited checks on majority power
- **Examples**: Denmark, Sweden, New Zealand

**Bicameral (Two Chambers)**
- **Upper House Functions**: Regional representation, review of legislation, expertise
- **Lower House Functions**: Popular representation, government formation, budget
- **Examples**: United States (Senate/House), Germany (Bundestag/Bundesrat)

**Electoral Systems**

**Single-Member District Plurality (First-Past-the-Post)**
- **Mechanism**: Candidate with most votes wins
- **Advantages**: Stable majority governments, clear accountability
- **Disadvantages**: Disproportional results, tactical voting
- **Examples**: United Kingdom, United States, India

**Proportional Representation (PR)**
- **Mechanism**: Seats allocated based on vote percentage
- **Advantages**: Proportional results, minority representation
- **Disadvantages**: Coalition governments, complex negotiations
- **Types**: List PR, Mixed-Member Proportional, Single Transferable Vote

**Mixed Systems**
- **Combination**: Elements of both plurality and PR systems
- **Examples**: Germany (Mixed-Member Proportional), Japan

### Judicial Systems

**Common Law Systems**
- **Case Law**: Judicial precedents create law
- **Judge-Made Law**: Courts interpret and develop legal principles
- **Adversarial Process**: Competing parties present cases
- **Examples**: United States, United Kingdom, Australia

**Civil Law Systems**
- **Written Codes**: Comprehensive legal codes
- **Limited Judicial Discretion**: Judges apply rather than make law
- **Inquisitorial Process**: Judges actively investigate cases
- **Examples**: Germany, France, Japan

**Judicial Review**
- **Constitutional Review**: Courts assess law constitutionality
- **Strong Review**: Courts can strike down laws (US model)
- **Weak Review**: Courts can declare incompatibility (UK model)
- **No Review**: Courts cannot review legislative acts

### Federal vs. Unitary Systems

**Federal Systems**
- **Divided Sovereignty**: Power shared between national and subnational governments
- **Constitutional Protection**: Subnational powers constitutionally guaranteed
- **Examples**: United States, Germany, Canada, Australia

**Advantages**
- **Local Autonomy**: Decisions made closer to citizens
- **Policy Innovation**: Subnational experimentation
- **Minority Protection**: Regional minorities can govern locally
- **Check on Power**: Multiple levels of government

**Disadvantages**
- **Complexity**: Multiple levels of authority
- **Inequality**: Variation in services and policies
- **Coordination Problems**: Difficulty in national policy
- **Potential Conflict**: Between levels of government

**Unitary Systems**
- **Centralized Authority**: Power concentrated in national government
- **Administrative Decentralization**: Local governments as agents
- **Examples**: United Kingdom, France, Japan

**Advantages**
- **Efficiency**: Clear lines of authority
- **Equality**: Uniform policies and services
- **Coordination**: Easier national policy implementation
- **Accountability**: Clear responsibility for decisions

**Disadvantages**
- **Centralization**: Distance from local concerns
- **Uniformity**: Limited adaptation to local conditions
- **Concentration of Power**: Potential for abuse

### Political Parties and Party Systems

**Functions of Political Parties**
- **Representation**: Aggregating and articulating interests
- **Governance**: Organizing government and opposition
- **Recruitment**: Selecting and training political leaders
- **Accountability**: Providing choices for voters

**Party System Types**

**One-Party Systems**
- **Single Party Rule**: One party holds power
- **Limited Competition**: No meaningful electoral choice
- **Examples**: China (Communist Party), former Soviet Union

**Two-Party Systems**
- **Two Dominant Parties**: Effective competition between two parties
- **Alternating Power**: Parties take turns governing
- **Examples**: United States (Democrats/Republicans), United Kingdom (Conservative/Labour)

**Multi-Party Systems**
- **Multiple Viable Parties**: Several parties compete effectively
- **Coalition Governments**: Often necessary to form majority
- **Examples**: Germany, Netherlands, Israel

### Interest Groups and Civil Society

**Types of Interest Groups**
- **Economic Groups**: Business associations, labor unions
- **Professional Groups**: Bar associations, medical organizations
- **Public Interest Groups**: Environmental, consumer groups
- **Single-Issue Groups**: Focused on specific policies

**Influence Methods**
- **Lobbying**: Direct contact with government officials
- **Campaign Contributions**: Financial support for candidates
- **Grassroots Mobilization**: Organizing citizen pressure
- **Litigation**: Using courts to influence policy

### Comparing Political Systems

**Performance Criteria**
- **Effectiveness**: Ability to make and implement decisions
- **Responsiveness**: Reflection of citizen preferences
- **Accountability**: Ability to hold leaders responsible
- **Representation**: Inclusion of diverse groups and interests
- **Stability**: Continuity and predictability of governance

**Institutional Effects**
- **Electoral Systems**: Effect on party systems and representation
- **Executive-Legislative Relations**: Impact on policy-making
- **Federal Structure**: Influence on policy variation and implementation
- **Judicial Role**: Effect on rights protection and policy

Understanding how different institutional arrangements affect political outcomes helps explain why countries with similar cultures and economic conditions may have very different political processes and policy outcomes.`
        },
        {
          id: "government-comparison",
          title: "Comparing Political Systems",
          description: "Interactive analysis of different governmental structures and their effects",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "system-comparison",
            instructions: "Compare different political systems across various dimensions and analyze how institutional design affects political outcomes.",
            systems: [
              {
                country: "United States",
                executive: "Presidential",
                legislature: "Bicameral",
                electoral: "First-past-the-post",
                parties: "Two-party dominant",
                structure: "Federal"
              },
              {
                country: "United Kingdom",
                executive: "Parliamentary",
                legislature: "Bicameral",
                electoral: "First-past-the-post",
                parties: "Multi-party with two dominant",
                structure: "Unitary"
              },
              {
                country: "Germany",
                executive: "Parliamentary",
                legislature: "Bicameral",
                electoral: "Mixed-member proportional",
                parties: "Multi-party coalition",
                structure: "Federal"
              }
            ],
            hints: [
              "Consider how electoral systems affect party systems",
              "Think about how executive systems influence accountability",
              "Analyze the relationship between structure and representation"
            ]
          }
        }
      ],
      quiz: {
        id: "comparative-government-quiz",
        title: "Comparative Government Quiz",
        description: "Test your knowledge of different government systems and institutions",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In a parliamentary system, who chooses the chief executive?",
            options: [
              "Direct popular vote",
              "Electoral college",
              "The legislature",
              "The judiciary"
            ],
            correctAnswer: 2,
            explanation: "In parliamentary systems, the chief executive (prime minister) is chosen by the legislature, typically the leader of the majority party or coalition."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main advantage of proportional representation electoral systems?",
            options: [
              "Produces stable majority governments",
              "Ensures all votes count equally",
              "Simplifies the voting process",
              "Reduces campaign costs"
            ],
            correctAnswer: 1,
            explanation: "Proportional representation ensures that the percentage of seats a party receives matches its percentage of votes, making all votes count equally."
          }
        ]
      }
    },

    {
      id: "american-government",
      title: "American Government and Politics",
      description: "Structure and processes of the United States government system",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "constitution-federalism",
          title: "Constitution and Federalism",
          description: "Constitutional foundations and federal-state relationships in the US",
          type: "video",
          duration: 30
        },
        {
          id: "us-political-institutions",
          title: "US Political Institutions",
          description: "Congress, presidency, courts, and bureaucracy in American government",
          type: "text",
          duration: 45,
          textContent: `# US Political Institutions

## The Constitutional Framework

### Founding Principles

**Constitutional Convention (1787)**
- **Articles of Confederation**: Weaknesses that led to constitutional reform
- **Competing Plans**: Virginia Plan vs. New Jersey Plan
- **Great Compromise**: Bicameral legislature with different representation
- **Three-Fifths Compromise**: Slavery and representation issues

**Core Constitutional Principles**
- **Separation of Powers**: Legislative, executive, judicial branches
- **Checks and Balances**: Each branch can limit others' power
- **Federalism**: Shared sovereignty between national and state governments
- **Limited Government**: Constraints on government power
- **Popular Sovereignty**: Government derives authority from people

### The Legislative Branch

**Structure and Organization**
- **House of Representatives**: 435 members, 2-year terms
- **Senate**: 100 members, 6-year terms
- **Leadership**: Speaker of House, Senate Majority/Minority Leaders
- **Committee System**: Standing, select, joint, and conference committees

**Powers and Functions**
- **Legislative Powers**: Making federal law, taxation, spending
- **Oversight**: Monitoring executive branch implementation
- **Representation**: Constituency service and interest articulation
- **Investigation**: Congressional hearings and inquiries

**Legislative Process**
- **Bill Introduction**: House or Senate introduction
- **Committee Action**: Review, markup, and voting
- **Floor Action**: Debate and voting by full chamber
- **Conference Committee**: Reconciling House and Senate versions
- **Presidential Action**: Signing or vetoing legislation

### The Executive Branch

**The Presidency**
- **Constitutional Qualifications**: Age, citizenship, residency requirements
- **Election Process**: Electoral College system
- **Powers**: Commander-in-Chief, foreign policy, law execution
- **Roles**: Head of state, head of government, party leader

**Presidential Powers**
- **Formal Powers**: Enumerated in Constitution
  - **Military**: Command of armed forces
  - **Diplomatic**: Treaty negotiation, foreign recognition
  - **Administrative**: Appointment power, law execution
  - **Legislative**: Veto power, State of Union, legislative agenda
- **Informal Powers**: Not explicitly stated but developed over time
  - **Persuasion**: "Bully pulpit" and public appeals
  - **Executive Orders**: Administrative directives
  - **Executive Privilege**: Withholding information from other branches

**Executive Office of the President**
- **White House Staff**: Chief of Staff, advisors, communications
- **Office of Management and Budget**: Budget preparation and oversight
- **National Security Council**: Foreign policy coordination
- **Council of Economic Advisers**: Economic policy advice

**The Bureaucracy**
- **Federal Departments**: Cabinet-level executive departments
- **Independent Agencies**: NASA, EPA, Social Security Administration
- **Regulatory Agencies**: FCC, SEC, Federal Reserve
- **Government Corporations**: Postal Service, Amtrak

**Bureaucratic Functions**
- **Implementation**: Executing laws passed by Congress
- **Regulation**: Creating and enforcing rules
- **Service Delivery**: Providing services to citizens
- **Policy Making**: Administrative discretion in rule-making

### The Judicial Branch

**Federal Court Structure**
- **Supreme Court**: Nine justices, final appellate court
- **Courts of Appeals**: Thirteen circuits, intermediate appellate courts
- **District Courts**: 94 districts, trial courts of general jurisdiction
- **Specialized Courts**: Tax Court, Court of International Trade

**Judicial Powers**
- **Judicial Review**: Power to declare laws or actions unconstitutional
- **Statutory Interpretation**: Determining meaning of federal laws
- **Constitutional Interpretation**: Applying Constitution to specific cases
- **Due Process**: Ensuring fair legal procedures

**Supreme Court**
- **Case Selection**: Writ of certiorari, discretionary jurisdiction
- **Decision-Making Process**: Oral arguments, conferences, opinions
- **Types of Opinions**: Majority, concurring, dissenting
- **Precedent**: Stare decisis and constitutional interpretation

**Judicial Selection**
- **Presidential Nomination**: President nominates federal judges
- **Senate Confirmation**: Advice and consent role
- **Life Tenure**: Service "during good behavior"
- **Political Considerations**: Ideology, representation, qualifications

### Federalism in Practice

**Division of Powers**
- **National Powers**: Enumerated, implied, and inherent powers
- **State Powers**: Reserved powers under Tenth Amendment
- **Concurrent Powers**: Shared between national and state governments
- **Prohibited Powers**: Limitations on both levels

**Evolution of Federalism**
- **Dual Federalism**: Layer cake model, clear separation
- **Cooperative Federalism**: Marble cake model, shared responsibilities
- **New Federalism**: Devolution of power to states
- **Contemporary Federalism**: Mix of cooperation and competition

**Federal-State Relations**
- **Supremacy Clause**: Federal law supreme when in conflict
- **Preemption**: Federal law overriding state law
- **Mandates**: Federal requirements for state compliance
- **Grants-in-Aid**: Federal funding with conditions

### Political Parties and Elections

**Two-Party System**
- **Historical Development**: Federalists vs. Democratic-Republicans
- **Contemporary Parties**: Democrats and Republicans
- **Third Parties**: Challenges and limited success
- **Party Functions**: Recruitment, organization, policy coordination

**Electoral Process**
- **Presidential Elections**: Primary system, general election, Electoral College
- **Congressional Elections**: House and Senate campaigns
- **Campaign Finance**: Individual contributions, PACs, Super PACs
- **Voting Behavior**: Partisan identification, candidate evaluation, issues

**Interest Groups and Lobbying**
- **Types of Groups**: Economic, ideological, professional, governmental
- **Lobbying Strategies**: Direct lobbying, grassroots mobilization, coalition building
- **Regulation**: Lobbying Disclosure Act, ethics rules
- **Iron Triangles**: Relationships between agencies, committees, and interest groups

### Civil Liberties and Civil Rights

**Bill of Rights**
- **First Amendment**: Freedom of speech, religion, press, assembly
- **Criminal Justice**: Fourth, Fifth, Sixth, Eighth Amendments
- **Other Rights**: Third, Seventh, Ninth, Tenth Amendments

**Civil Rights Evolution**
- **Reconstruction Amendments**: 13th, 14th, 15th Amendments
- **Jim Crow Era**: Plessy v. Ferguson and "separate but equal"
- **Civil Rights Movement**: Brown v. Board, Civil Rights Act of 1964
- **Contemporary Issues**: Voting rights, affirmative action, LGBTQ+ rights

Understanding American government requires recognizing how constitutional design creates both cooperation and conflict among institutions, shaping policy outcomes and democratic representation.`
        },
        {
          id: "policy-process",
          title: "The Policy-Making Process",
          description: "How policies are made, implemented, and evaluated in the US system",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "policy-simulation",
            instructions: "Trace the policy-making process from agenda setting through implementation and evaluation.",
            stages: [
              {
                stage: "Agenda Setting",
                description: "How issues gain political attention",
                factors: ["Media coverage", "Interest group pressure", "Focusing events", "Political climate"]
              },
              {
                stage: "Policy Formation",
                description: "Developing policy alternatives",
                factors: ["Expert analysis", "Stakeholder input", "Political feasibility", "Cost-benefit analysis"]
              },
              {
                stage: "Adoption",
                description: "Formal approval of policy",
                factors: ["Legislative process", "Executive approval", "Judicial review", "Political support"]
              },
              {
                stage: "Implementation",
                description: "Putting policy into practice",
                factors: ["Bureaucratic capacity", "State cooperation", "Resource allocation", "Compliance monitoring"]
              },
              {
                stage: "Evaluation",
                description: "Assessing policy effectiveness",
                factors: ["Outcome measurement", "Stakeholder feedback", "Unintended consequences", "Political review"]
              }
            ],
            hints: [
              "Consider how different institutions influence each stage",
              "Think about the role of federalism in implementation",
              "Look at how political factors shape policy choices"
            ]
          }
        }
      ],
      quiz: {
        id: "american-government-quiz",
        title: "American Government Quiz",
        description: "Test your knowledge of US governmental institutions and processes",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which constitutional principle allows each branch of government to limit the powers of the others?",
            options: [
              "Separation of powers",
              "Checks and balances",
              "Federalism",
              "Popular sovereignty"
            ],
            correctAnswer: 1,
            explanation: "Checks and balances allows each branch to limit the others' power, while separation of powers divides government functions among the branches."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the primary function of congressional committees?",
            options: [
              "To impeach federal officials",
              "To review and modify legislation",
              "To confirm presidential appointments",
              "To interpret the Constitution"
            ],
            correctAnswer: 1,
            explanation: "Congressional committees primarily review, modify, and recommend action on proposed legislation before it goes to the full chamber for a vote."
          }
        ]
      }
    },

    {
      id: "public-policy",
      title: "Public Policy Analysis",
      description: "How public policies are developed, implemented, and evaluated",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "policy-cycle",
          title: "The Policy Process",
          description: "Understanding the stages of public policy development",
          type: "video",
          duration: 30
        },
        {
          id: "policy-analysis-methods",
          title: "Policy Analysis and Evaluation",
          description: "Tools and methods for analyzing public policy effectiveness",
          type: "text",
          duration: 45,
          textContent: `# Policy Analysis and Evaluation

## Understanding Public Policy

### Definition and Scope

**Public Policy Defined**
Public policy refers to the actions, decisions, and commitments made by government authorities to address public problems or pursue public goals.

**Characteristics of Public Policy**
- **Government Action**: Involves official governmental authority
- **Public Problems**: Addresses issues affecting society
- **Goal-Oriented**: Designed to achieve specific objectives
- **Authoritative**: Backed by government power and resources

**Types of Public Policy**
- **Regulatory Policy**: Rules governing behavior (environmental regulations)
- **Distributive Policy**: Benefits to specific groups (farm subsidies)
- **Redistributive Policy**: Transfer resources between groups (welfare programs)
- **Constituent Policy**: Government operations (civil service rules)

### The Policy Process Model

**Stage 1: Problem Recognition and Agenda Setting**

**Problem Recognition**
- **Indicators**: Statistical measures showing problem severity
- **Focusing Events**: Crises that draw attention to issues
- **Feedback**: Information about existing policy performance

**Agenda Setting**
- **Systemic Agenda**: All issues receiving public attention
- **Institutional Agenda**: Issues actively considered by government
- **Decision Agenda**: Issues ready for authoritative decision

**Factors Influencing Agenda Setting**
- **Media Coverage**: Public attention and framing
- **Interest Group Activity**: Lobbying and advocacy
- **Political Leadership**: Officials highlighting issues
- **Public Opinion**: Citizen concerns and priorities
- **External Events**: Crises or changing conditions

**Stage 2: Policy Formation and Design**

**Policy Formation Process**
- **Alternative Generation**: Developing policy options
- **Alternative Evaluation**: Assessing feasibility and effectiveness
- **Alternative Selection**: Choosing preferred approach

**Policy Design Elements**
- **Goals and Objectives**: What policy aims to achieve
- **Target Population**: Who is affected by policy
- **Policy Tools**: Methods for achieving objectives
- **Implementation Structure**: Organizations and procedures

**Policy Tools**
- **Regulation**: Rules and standards
- **Economic Incentives**: Taxes, subsidies, grants
- **Information**: Education, persuasion, labeling
- **Direct Provision**: Government services and programs
- **Voluntary Measures**: Cooperation and self-regulation

**Stage 3: Policy Adoption and Legitimation**

**Adoption Process**
- **Legislative Action**: Bills, hearings, voting
- **Executive Decision**: Presidential orders, agency rules
- **Judicial Decision**: Court rulings and interpretations
- **Referendum/Initiative**: Direct citizen voting

**Factors Affecting Adoption**
- **Political Support**: Coalition building and bargaining
- **Institutional Rules**: Procedures and requirements
- **Timing**: Windows of opportunity
- **Resources**: Available funding and capacity

**Stage 4: Policy Implementation**

**Implementation Defined**
The process of putting adopted policies into effect through government action and citizen compliance.

**Implementation Challenges**
- **Resource Constraints**: Insufficient funding or personnel
- **Coordination Problems**: Multiple agencies and levels
- **Compliance Issues**: Resistance from target groups
- **Ambiguous Goals**: Unclear or conflicting objectives

**Implementation Approaches**
- **Top-Down**: Central control and direction
- **Bottom-Up**: Local discretion and adaptation
- **Network Approach**: Collaboration among multiple actors

**Factors Affecting Implementation Success**
- **Clear Goals**: Well-defined objectives and standards
- **Adequate Resources**: Sufficient funding and personnel
- **Strong Leadership**: Committed and capable managers
- **Political Support**: Sustained backing from key officials
- **Effective Organization**: Appropriate structure and procedures
- **Target Group Cooperation**: Willing compliance and participation

**Stage 5: Policy Evaluation**

**Purposes of Evaluation**
- **Accountability**: Demonstrating responsible use of resources
- **Improvement**: Identifying ways to enhance performance
- **Learning**: Building knowledge about what works
- **Political**: Justifying or criticizing policies

**Types of Evaluation**
- **Process Evaluation**: Assessing implementation activities
- **Outcome Evaluation**: Measuring policy results
- **Impact Evaluation**: Determining causal effects
- **Cost-Benefit Analysis**: Comparing costs and benefits

### Policy Analysis Methods

**Rational Decision-Making Model**
- **Problem Definition**: Clearly specify the issue
- **Goal Setting**: Establish objectives and criteria
- **Alternative Generation**: Identify all possible options
- **Alternative Evaluation**: Assess costs, benefits, and feasibility
- **Selection**: Choose optimal alternative
- **Implementation**: Put decision into effect
- **Evaluation**: Monitor results and adjust

**Cost-Benefit Analysis**
- **Monetizing Costs and Benefits**: Converting effects to dollar values
- **Present Value**: Adjusting for time preferences
- **Net Present Value**: Benefits minus costs
- **Benefit-Cost Ratio**: Benefits divided by costs
- **Sensitivity Analysis**: Testing assumptions

**Cost-Effectiveness Analysis**
- **Fixed Budget**: Finding most effective use of resources
- **Fixed Effectiveness**: Finding least costly way to achieve goals
- **Effectiveness Measures**: Non-monetary outcome indicators

**Multi-Criteria Analysis**
- **Multiple Objectives**: Considering various goals and values
- **Weighting**: Assigning importance to different criteria
- **Scoring**: Rating alternatives on each criterion
- **Aggregation**: Combining scores for overall assessment

### Policy Networks and Subsystems

**Policy Networks**
- **Issue Networks**: Loose coalitions around specific issues
- **Iron Triangles**: Close relationships among agencies, committees, interest groups
- **Advocacy Coalitions**: Groups sharing policy beliefs
- **Epistemic Communities**: Knowledge-based expert networks

**Policy Subsystems**
- **Substantive Scope**: Issue area or program domain
- **Functional Scope**: Geographic or institutional boundaries
- **Participants**: Actors regularly involved in policy area
- **Relationships**: Patterns of interaction and influence

### Contemporary Policy Challenges

**Globalization**
- **International Interdependence**: Cross-border policy effects
- **Regulatory Competition**: Competition for investment and jobs
- **Global Governance**: International institutions and agreements

**Technology and Innovation**
- **Rapid Change**: Difficulty keeping pace with technological development
- **Regulatory Lag**: Time delay in policy responses
- **Digital Divide**: Unequal access to technology benefits

**Complexity and Uncertainty**
- **Wicked Problems**: Issues resistant to simple solutions
- **Unintended Consequences**: Unforeseen policy effects
- **Scientific Uncertainty**: Incomplete knowledge about problems

**Governance Networks**
- **Multi-Level Governance**: Involvement of multiple government levels
- **Public-Private Partnerships**: Collaboration with private sector
- **Citizen Participation**: Involving public in policy process

### Case Studies in Policy Analysis

**Environmental Policy**
- **Clean Air Act**: Regulatory approach to pollution control
- **Cap and Trade**: Market-based emissions reduction
- **Environmental Impact Assessment**: Procedural requirements

**Healthcare Policy**
- **Medicare/Medicaid**: Social insurance and welfare approaches
- **Affordable Care Act**: Comprehensive health system reform
- **Public Health**: Prevention and population health strategies

**Economic Policy**
- **Fiscal Policy**: Taxation and spending decisions
- **Monetary Policy**: Interest rates and money supply
- **Regulatory Policy**: Financial system oversight

Understanding policy analysis provides tools for evaluating government actions and improving public problem-solving capacity.`
        },
        {
          id: "policy-evaluation",
          title: "Policy Evaluation Exercise",
          description: "Evaluate the effectiveness of a specific public policy using analytical frameworks",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "policy-evaluation",
            instructions: "Analyze a public policy using multiple evaluation criteria and methods.",
            policies: [
              {
                name: "Universal Basic Income Pilot Program",
                goals: ["Reduce poverty", "Simplify welfare system", "Support economic security"],
                outcomes: ["Mixed poverty reduction", "Administrative cost savings", "Increased recipient satisfaction"],
                costs: "$50 million annually",
                challenges: ["Political opposition", "Work disincentive concerns", "Funding sustainability"]
              },
              {
                name: "Carbon Tax Policy",
                goals: ["Reduce greenhouse gas emissions", "Generate revenue", "Promote clean energy"],
                outcomes: ["15% emissions reduction", "Significant revenue generation", "Economic sector impacts"],
                costs: "$2 billion implementation",
                challenges: ["Industry resistance", "Regional economic effects", "International competitiveness"]
              }
            ],
            evaluationCriteria: [
              "Effectiveness: Did the policy achieve its goals?",
              "Efficiency: Were resources used optimally?",
              "Equity: Were costs and benefits fairly distributed?",
              "Feasibility: Is the policy politically and administratively viable?",
              "Sustainability: Can the policy be maintained long-term?"
            ],
            hints: [
              "Consider both intended and unintended consequences",
              "Think about different stakeholder perspectives",
              "Analyze short-term vs. long-term effects"
            ]
          }
        }
      ],
      quiz: {
        id: "public-policy-quiz",
        title: "Public Policy Quiz",
        description: "Test your understanding of policy processes and analysis methods",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary purpose of cost-benefit analysis in policy making?",
            options: [
              "To determine political feasibility",
              "To compare costs and benefits in monetary terms",
              "To measure public opinion",
              "To assess constitutional validity"
            ],
            correctAnswer: 1,
            explanation: "Cost-benefit analysis converts policy costs and benefits into monetary terms to enable systematic comparison and evaluation of alternatives."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which stage of the policy process involves putting adopted policies into practice?",
            options: [
              "Agenda setting",
              "Policy formation",
              "Policy adoption",
              "Policy implementation"
            ],
            correctAnswer: 3,
            explanation: "Policy implementation is the stage where adopted policies are put into practice through government action and citizen compliance."
          }
        ]
      }
    },

    {
      id: "international-relations",
      title: "International Relations and Global Politics",
      description: "Understanding relationships between nations and global political processes",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "ir-theories",
          title: "Theories of International Relations",
          description: "Realism, liberalism, constructivism, and other IR theoretical approaches",
          type: "video",
          duration: 35
        },
        {
          id: "global-governance",
          title: "International Organizations and Global Governance",
          description: "UN, NATO, EU, and other international institutions",
          type: "text",
          duration: 45,
          textContent: `# International Organizations and Global Governance

## Understanding Global Politics

### The International System

**Characteristics of the International System**
- **Anarchy**: No supranational authority above states
- **Sovereignty**: States' supreme authority within territories
- **Self-Help**: States must provide for their own security
- **Balance of Power**: Distribution of capabilities among states

**Actors in International Relations**
- **States**: Primary actors with sovereignty and territory
- **International Organizations**: Formal institutions with state membership
- **Non-Governmental Organizations**: Private actors with international scope
- **Multinational Corporations**: Economic actors across borders
- **Individuals**: Leaders, diplomats, activists, terrorists

### Theoretical Approaches to International Relations

**Realism**
- **Core Assumptions**: States are primary actors, international system is anarchic
- **Power Politics**: States seek to maximize power and security
- **Security Dilemma**: Measures to increase security may threaten others
- **Balance of Power**: States balance against threats
- **Key Thinkers**: Hans Morgenthau, Kenneth Waltz, John Mearsheimer

**Liberalism/Neoliberal Institutionalism**
- **International Cooperation**: States can cooperate for mutual benefit
- **Institutions Matter**: International organizations facilitate cooperation
- **Economic Interdependence**: Trade creates incentives for peace
- **Democratic Peace**: Democracies rarely fight each other
- **Key Thinkers**: Robert Keohane, Joseph Nye, Michael Doyle

**Constructivism**
- **Ideas and Norms**: Beliefs shape state behavior
- **Social Construction**: International system created through interaction
- **Identity Formation**: State interests determined by identity
- **Norm Entrepreneurs**: Actors promote new international norms
- **Key Thinkers**: Alexander Wendt, Martha Finnemore, Peter Katzenstein

**Critical Theories**
- **Marxist Approaches**: Focus on economic class and exploitation
- **Feminist IR**: Gender and women's experiences in international politics
- **Post-Colonial Theory**: Legacy of colonialism in global relations
- **Green Theory**: Environmental concerns and sustainability

### International Organizations

**Types of International Organizations**

**Global Organizations**
- **Universal Membership**: Open to all states
- **General Purpose**: Broad mandate and activities
- **Examples**: United Nations, World Bank, International Monetary Fund

**Regional Organizations**
- **Geographic Scope**: Limited to specific regions
- **Various Purposes**: Security, economic, political integration
- **Examples**: European Union, ASEAN, African Union, OAS

**Functional Organizations**
- **Issue-Specific**: Focus on particular problems or sectors
- **Technical Expertise**: Specialized knowledge and standards
- **Examples**: World Health Organization, International Labour Organization

### The United Nations System

**Principal Organs**

**General Assembly**
- **Membership**: All UN member states (193 countries)
- **Functions**: Deliberation, budget approval, recommendation
- **Voting**: One state, one vote; majority or two-thirds rule
- **Limitations**: Recommendations only, no enforcement power

**Security Council**
- **Membership**: 15 members (5 permanent, 10 non-permanent)
- **Permanent Members**: US, UK, France, Russia, China (P5)
- **Powers**: Peacekeeping, sanctions, enforcement actions
- **Veto Power**: P5 can block substantive decisions

**Economic and Social Council (ECOSOC)**
- **Coordination**: Economic and social work of UN system
- **Membership**: 54 members elected by General Assembly
- **Subsidiary Bodies**: Commissions on human rights, status of women

**Trusteeship Council**
- **Historical Role**: Overseeing trust territories
- **Current Status**: Suspended operations (1994)

**International Court of Justice**
- **Membership**: 15 judges elected by GA and SC
- **Jurisdiction**: Disputes between states, advisory opinions
- **Limitations**: States must consent to jurisdiction

**Secretariat**
- **Secretary-General**: Chief administrative officer
- **International Civil Service**: Staff from member countries
- **Functions**: Administrative support, peacekeeping operations

**UN Specialized Agencies**
- **WHO**: World Health Organization
- **UNESCO**: Education, Science, Culture
- **ILO**: International Labour Organization
- **IMF**: International Monetary Fund
- **World Bank**: Development financing

### Regional Organizations

**European Union**
- **Integration Model**: Supranational governance
- **Institutions**: Commission, Council, Parliament, Court
- **Policies**: Single market, monetary union, common policies
- **Challenges**: Brexit, sovereignty concerns, economic disparities

**North Atlantic Treaty Organization (NATO)**
- **Collective Security**: Article 5 mutual defense commitment
- **Membership**: 30 countries (as of 2021)
- **Evolution**: Cold War alliance to crisis management
- **Expansion**: Enlargement to Eastern Europe

**Association of Southeast Asian Nations (ASEAN)**
- **ASEAN Way**: Non-interference, consensus, informal consultation
- **Economic Integration**: ASEAN Free Trade Area, Economic Community
- **Security**: ASEAN Regional Forum, conflict prevention

**African Union**
- **Successor**: Organization of African Unity (2001)
- **Goals**: Integration, development, peace and security
- **Institutions**: Commission, Parliament, Peace and Security Council
- **Challenges**: Capacity constraints, conflict resolution

### International Law and Norms

**Sources of International Law**
- **Treaties**: Written agreements between states
- **Custom**: General practice accepted as law
- **General Principles**: Common legal principles across systems
- **Judicial Decisions**: Court rulings and precedents
- **Scholarly Writings**: Authoritative commentary

**Types of International Law**
- **Public International Law**: Relations between states
- **Private International Law**: Cross-border private matters
- **Supranational Law**: European Union law
- **Soft Law**: Non-binding guidelines and standards

**Enforcement Challenges**
- **No World Government**: Decentralized enforcement
- **State Consent**: Voluntary compliance and jurisdiction
- **Power Politics**: Strong states can ignore legal obligations
- **Diplomatic Solutions**: Negotiation and compromise preferred

### Global Governance Issues

**Security Governance**
- **Collective Security**: UN Charter system
- **Peacekeeping**: Blue helmet operations
- **Peacebuilding**: Post-conflict reconstruction
- **Responsibility to Protect**: International intervention doctrine

**Economic Governance**
- **Trade**: World Trade Organization, regional trade agreements
- **Finance**: International Monetary Fund, World Bank
- **Development**: UN Development Programme, Millennium Development Goals
- **Regulation**: Basel banking standards, tax cooperation

**Environmental Governance**
- **Climate Change**: Paris Agreement, UNFCCC
- **Biodiversity**: Convention on Biological Diversity
- **Ozone Depletion**: Montreal Protocol success story
- **Ocean Governance**: UN Convention on Law of Sea

**Human Rights Governance**
- **Universal Declaration**: UN human rights framework
- **Treaty Bodies**: Monitoring compliance with human rights treaties
- **Regional Systems**: European, Inter-American, African human rights courts
- **Civil Society**: NGO advocacy and monitoring

### Contemporary Challenges

**Globalization and Governance**
- **Economic Integration**: Trade, investment, financial flows
- **Technological Connectivity**: Internet, communications, transportation
- **Cultural Exchange**: Ideas, values, lifestyles
- **Governance Gaps**: National governments, global problems

**Power Transitions**
- **Rising Powers**: China, India, Brazil's growing influence
- **Declining Hegemony**: US relative power decline
- **Multipolarity**: Multiple centers of power
- **Institutional Adaptation**: Reforming global governance

**Non-State Actors**
- **Multinational Corporations**: Economic power and influence
- **NGOs**: Advocacy, service provision, monitoring
- **Terrorist Organizations**: Asymmetric threats to states
- **Transnational Networks**: Criminal, religious, ideological groups

**Global Problems**
- **Climate Change**: Collective action challenges
- **Pandemic Disease**: COVID-19 and global health governance
- **Cybersecurity**: Internet governance and cyber warfare
- **Migration**: Refugee protection and labor mobility

### Effectiveness of International Organizations

**Factors Affecting Effectiveness**
- **Clear Mandate**: Well-defined goals and responsibilities
- **Adequate Resources**: Funding and operational capacity
- **Member Support**: Political commitment and compliance
- **Institutional Design**: Decision-making rules and procedures
- **External Environment**: Favorable conditions for cooperation

**Measuring Effectiveness**
- **Output**: Activities and programs produced
- **Outcome**: Direct results of organizational activities
- **Impact**: Long-term effects on global problems
- **Legitimacy**: Acceptance and moral authority

**Reforms and Future Directions**
- **UN Security Council Reform**: Expansion and veto power changes
- **Global Economic Governance**: G20, BRICS coordination
- **Regional Integration**: Deepening cooperation within regions
- **Multi-Stakeholder Governance**: Including non-state actors

International organizations play crucial roles in addressing global challenges, though their effectiveness varies based on design, resources, and member state cooperation. Understanding global governance helps explain how the international community attempts to manage interdependence and shared problems.`
        },
        {
          id: "crisis-simulation",
          title: "International Crisis Simulation",
          description: "Role-play exercise simulating international diplomatic negotiations",
          type: "social-sciences-interactive",
          duration: 35,
          interactiveData: {
            exerciseType: "diplomatic-simulation",
            instructions: "Participate in a simulated international crisis requiring diplomatic negotiation and multilateral cooperation.",
            scenario: {
              title: "Climate Change Conference",
              description: "Major powers negotiate a new international climate agreement",
              roles: ["United States", "European Union", "China", "India", "Small Island States", "Oil Producers"],
              issues: ["Emission reduction targets", "Financial assistance", "Technology transfer", "Compliance monitoring"],
              constraints: ["Domestic political pressures", "Economic costs", "National interests", "Time pressure"]
            },
            negotiationRounds: [
              {
                round: 1,
                focus: "Initial position statements and demands",
                outcome: "Identification of key disagreements"
              },
              {
                round: 2,
                focus: "Bilateral consultations and alliance building",
                outcome: "Formation of negotiating coalitions"
              },
              {
                round: 3,
                focus: "Compromise proposals and trade-offs",
                outcome: "Draft agreement elements"
              },
              {
                round: 4,
                focus: "Final negotiations and agreement",
                outcome: "Signed agreement or negotiation breakdown"
              }
            ],
            hints: [
              "Consider each country's domestic constraints and interests",
              "Look for mutually beneficial trade-offs and side payments",
              "Think about the role of international law and institutions"
            ]
          }
        }
      ],
      quiz: {
        id: "international-relations-quiz",
        title: "International Relations Quiz",
        description: "Test your knowledge of IR theories, organizations, and global governance",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to realist theory, what is the primary motivation of states in international relations?",
            options: [
              "Promoting human rights",
              "Maximizing economic welfare",
              "Ensuring security and survival",
              "Spreading democracy"
            ],
            correctAnswer: 2,
            explanation: "Realist theory holds that states' primary motivation is ensuring their security and survival in an anarchic international system."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which UN Security Council members have permanent seats and veto power?",
            options: [
              "US, UK, Germany, Japan, India",
              "US, UK, France, Russia, China",
              "US, UK, France, Germany, Brazil",
              "US, Russia, China, India, Japan"
            ],
            correctAnswer: 1,
            explanation: "The five permanent members of the UN Security Council with veto power are the United States, United Kingdom, France, Russia, and China."
          }
        ]
      }
    },

    {
      id: "political-participation",
      title: "Political Participation and Elections",
      description: "Voting behavior, campaigns, and citizen participation in democratic processes",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "voting-behavior",
          title: "Voting and Electoral Behavior",
          description: "Why people vote and how they make electoral choices",
          type: "video",
          duration: 30
        },
        {
          id: "campaign-elections",
          title: "Campaigns, Elections, and Democratic Participation",
          description: "Electoral systems, campaign strategies, and democratic engagement",
          type: "text",
          duration: 45,
          textContent: `# Campaigns, Elections, and Democratic Participation

## Democratic Participation

### Concepts of Democracy

**Defining Democracy**
- **Popular Sovereignty**: Government derives authority from the people
- **Political Equality**: Equal political rights for all citizens
- **Majority Rule with Minority Rights**: Majority governs with protection for minorities
- **Competitive Elections**: Regular, free, and fair electoral competition

**Types of Democracy**
- **Direct Democracy**: Citizens participate directly in decision-making
- **Representative Democracy**: Citizens elect representatives to govern
- **Liberal Democracy**: Constitutional protections for individual rights
- **Social Democracy**: Democratic politics with extensive welfare state

**Models of Democratic Participation**
- **Elite Democracy**: Limited participation, elite competition
- **Pluralist Democracy**: Group competition and bargaining
- **Participatory Democracy**: Widespread citizen involvement
- **Deliberative Democracy**: Informed discussion and reasoning

### Electoral Systems

**Functions of Elections**
- **Representation**: Translating citizen preferences into government
- **Accountability**: Holding officials responsible for performance
- **Legitimacy**: Providing mandate for government action
- **Choice**: Offering alternatives to voters

**Types of Electoral Systems**

**Plurality Systems**
- **First-Past-the-Post**: Candidate with most votes wins
- **Advantages**: Clear winners, stable governments, local representation
- **Disadvantages**: Disproportional results, wasted votes, tactical voting
- **Examples**: United States, United Kingdom, Canada

**Majority Systems**
- **Two-Round System**: Second round if no majority in first
- **Alternative Vote**: Ranked choice voting
- **Advantages**: Majority winner, broader coalitions
- **Disadvantages**: Complex, potential instability
- **Examples**: France (two-round), Australia (alternative vote)

**Proportional Representation**
- **Party List PR**: Votes for party lists, seats allocated proportionally
- **Single Transferable Vote**: Multi-member districts with ranked voting
- **Advantages**: Proportional results, minority representation
- **Disadvantages**: Coalition governments, complex negotiations
- **Examples**: Germany, Netherlands, Ireland

**Mixed Systems**
- **Mixed-Member Proportional**: Combination of district and list seats
- **Parallel Systems**: Separate plurality and PR elections
- **Examples**: Germany (MMP), Japan, Russia (parallel)

### Voting Behavior

**Factors Influencing Vote Choice**

**Sociological Factors**
- **Social Class**: Income, occupation, education effects
- **Religion**: Religious affiliation and observance
- **Race and Ethnicity**: Group identification and interests
- **Gender**: Gender gap in political preferences
- **Age**: Generational differences in political attitudes

**Psychological Factors**
- **Partisan Identification**: Long-term party loyalty
- **Political Efficacy**: Sense of political influence
- **Political Interest**: Attention to politics and campaigns
- **Ideology**: Liberal-conservative orientation

**Short-Term Forces**
- **Candidate Evaluations**: Personal qualities and characteristics
- **Issue Positions**: Agreement with candidate/party policies
- **Campaign Effects**: Advertising, debates, media coverage
- **Economic Conditions**: Retrospective economic voting

**Voting Decision Models**

**Sociological Model (Columbia School)**
- **Social Determinism**: Social characteristics determine voting
- **Group Influence**: Family, peers, social networks
- **Stability**: Voting patterns reflect social structure

**Psychological Model (Michigan School)**
- **Partisan Identification**: Core psychological attachment
- **Funnel of Causality**: Long-term to short-term influences
- **Issue Voting**: Policy preferences affect vote choice

**Rational Choice Model**
- **Instrumental Voting**: Voting based on expected utility
- **Issue Voting**: Policy-oriented decision-making
- **Strategic Voting**: Tactical considerations in vote choice

**Retrospective Voting**
- **Performance Evaluation**: Judging incumbent performance
- **Economic Voting**: Rewards and punishments for economic conditions
- **Issue Ownership**: Party reputations on specific issues

### Political Campaigns

**Campaign Functions**
- **Information**: Educating voters about candidates and issues
- **Mobilization**: Encouraging voter turnout
- **Persuasion**: Changing voter preferences
- **Reinforcement**: Strengthening existing preferences

**Campaign Organization**
- **Professional Consultants**: Strategy, polling, advertising specialists
- **Volunteer Organizations**: Grassroots mobilization and support
- **Party Organizations**: Institutional support and coordination
- **Interest Groups**: Independent expenditures and endorsements

**Campaign Strategies**

**Targeting Strategies**
- **Swing Voters**: Persuading undecided voters
- **Base Mobilization**: Turning out core supporters
- **Geographic Targeting**: Focusing on competitive areas
- **Demographic Targeting**: Appealing to specific groups

**Message Strategies**
- **Positive Campaigning**: Promoting candidate qualifications and policies
- **Negative Campaigning**: Attacking opponent's record or character
- **Comparative Advertising**: Contrasting candidate positions
- **Issue Ownership**: Emphasizing party strengths

**Campaign Tactics**
- **Television Advertising**: Mass media persuasion
- **Digital Campaigning**: Social media, email, websites
- **Direct Contact**: Phone calls, door-to-door canvassing
- **Debates**: Candidate forums and discussions
- **Endorsements**: Support from organizations and leaders

### Campaign Finance

**Sources of Campaign Funding**
- **Individual Contributions**: Small and large donors
- **Political Action Committees (PACs)**: Interest group contributions
- **Party Committees**: Institutional party support
- **Self-Funding**: Candidates' personal wealth
- **Public Financing**: Government funding programs

**Regulation and Reform**
- **Contribution Limits**: Restrictions on donation amounts
- **Disclosure Requirements**: Transparency in funding sources
- **Public Financing**: Taxpayer-funded campaigns
- **Independent Expenditures**: Unlimited spending by outside groups

**Citizens United and Super PACs**
- **Independent Expenditures**: No limits on corporate/union spending
- **Super PACs**: Independent committees with unlimited fundraising
- **Dark Money**: Undisclosed funding through 501(c)(4) organizations
- **Coordination Rules**: Prohibitions on campaign coordination

### Voter Turnout and Participation

**Turnout Patterns**
- **Presidential vs. Off-Year**: Higher turnout in presidential elections
- **International Comparison**: US turnout lower than most democracies
- **Demographic Differences**: Age, education, income effects
- **Historical Trends**: Long-term decline in mid-20th century

**Factors Affecting Turnout**

**Individual-Level Factors**
- **Education**: Strong positive correlation with voting
- **Age**: Older voters more likely to participate
- **Income**: Higher income associated with voting
- **Civic Duty**: Sense of obligation to vote

**Political Factors**
- **Competitiveness**: Close elections increase turnout
- **Candidate Quality**: Attractive candidates mobilize voters
- **Campaigning**: Contact and mobilization efforts
- **Issues**: Salient issues can drive participation

**Institutional Factors**
- **Registration Requirements**: Barriers to voter registration
- **Voting Procedures**: Convenience of voting process
- **Electoral System**: Proportional representation increases turnout
- **Compulsory Voting**: Legal requirements to vote

**Voter Mobilization**
- **Get-Out-The-Vote (GOTV)**: Campaign efforts to increase turnout
- **Registration Drives**: Efforts to register new voters
- **Early Voting**: Extended voting periods
- **Vote-by-Mail**: Convenient voting procedures

### Other Forms of Political Participation

**Conventional Participation**
- **Contacting Officials**: Letters, emails, phone calls to representatives
- **Campaign Activities**: Volunteering, donating, attending events
- **Community Involvement**: Attending meetings, serving on boards
- **Interest Group Membership**: Joining organizations and associations

**Unconventional Participation**
- **Protests and Demonstrations**: Public expressions of political views
- **Civil Disobedience**: Deliberate law-breaking for political ends
- **Political Violence**: Extreme forms of political expression
- **Online Activism**: Digital political engagement and organizing

**Factors Affecting Participation**
- **Resources**: Time, money, civic skills
- **Motivation**: Political interest, efficacy, group identity
- **Opportunities**: Recruitment and mobilization
- **Institutional Context**: Political system openness and responsiveness

### Contemporary Challenges

**Declining Participation**
- **Voter Turnout**: Lower participation rates
- **Civic Engagement**: Reduced involvement in community organizations
- **Political Trust**: Declining confidence in institutions
- **Partisan Polarization**: Increasing ideological divisions

**Digital Democracy**
- **Online Participation**: Social media political engagement
- **E-Government**: Digital service delivery and interaction
- **Digital Divide**: Unequal access to technology
- **Misinformation**: False information and political manipulation

**Electoral Integrity**
- **Voter Suppression**: Efforts to reduce turnout
- **Gerrymandering**: Manipulating district boundaries
- **Foreign Interference**: External influence on elections
- **Election Administration**: Ensuring fair and accurate elections

**Campaign Finance Reform**
- **Money in Politics**: Influence of wealthy donors and corporations
- **Disclosure**: Transparency in political spending
- **Public Financing**: Alternative funding systems
- **Constitutional Constraints**: First Amendment and political speech

Understanding elections and political participation helps explain how democracies function and how citizens can effectively engage in the political process to influence government and policy.`
        },
        {
          id: "turnout-analysis",
          title: "Voter Turnout Analysis",
          description: "Analyze factors affecting voter turnout using real electoral data",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "turnout-analysis",
            instructions: "Examine voter turnout patterns and identify factors that increase or decrease political participation.",
            datasets: [
              {
                election: "2020 Presidential Election",
                overallTurnout: "66.6%",
                demographics: [
                  { group: "Age 18-29", turnout: "50%", change: "+11%" },
                  { group: "Age 65+", turnout: "76%", change: "+1%" },
                  { group: "College Educated", turnout: "80%", change: "+5%" },
                  { group: "High School Only", turnout: "55%", change: "+3%" }
                ]
              },
              {
                election: "2018 Midterm Election", 
                overallTurnout: "49.4%",
                demographics: [
                  { group: "Age 18-29", turnout: "36%", change: "+16%" },
                  { group: "Age 65+", turnout: "66%", change: "+4%" },
                  { group: "College Educated", turnout: "64%", change: "+12%" },
                  { group: "High School Only", turnout: "38%", change: "+8%" }
                ]
              }
            ],
            factors: [
              "Competitive races increase turnout",
              "Higher education correlates with voting",
              "Age is strongest predictor of participation",
              "Convenience voting methods help turnout"
            ],
            hints: [
              "Compare presidential vs. midterm turnout patterns",
              "Look at which groups showed biggest increases",
              "Consider what factors might explain demographic differences"
            ]
          }
        }
      ],
      quiz: {
        id: "political-participation-quiz",
        title: "Political Participation Quiz",
        description: "Test your knowledge of voting behavior, campaigns, and democratic participation",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which demographic characteristic is most strongly correlated with voter turnout?",
            options: [
              "Gender",
              "Race",
              "Age",
              "Region"
            ],
            correctAnswer: 2,
            explanation: "Age is the strongest demographic predictor of voter turnout, with older citizens consistently voting at much higher rates than younger citizens."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main advantage of proportional representation electoral systems?",
            options: [
              "Produces stable majority governments",
              "Ensures all votes count equally toward representation",
              "Simplifies the ballot for voters",
              "Reduces campaign costs"
            ],
            correctAnswer: 1,
            explanation: "Proportional representation ensures that parties receive seats in proportion to their vote share, making all votes count equally toward representation."
          }
        ]
      }
    },

    {
      id: "political-economy",
      title: "Political Economy",
      description: "The relationship between politics and economics, including economic policy and regulation",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "politics-economics-intersection",
          title: "Politics and Economics Intersection",
          description: "How political and economic systems interact and influence each other",
          type: "video",
          duration: 30
        },
        {
          id: "economic-policy",
          title: "Economic Policy and Regulation",
          description: "Government's role in economic management and market regulation",
          type: "text",
          duration: 45,
          textContent: `# Economic Policy and Regulation

## Political Economy Fundamentals

### Defining Political Economy

**Political Economy** is the study of the relationship between politics and economics, examining how political institutions, processes, and decisions affect economic outcomes, and how economic factors influence political behavior and institutions.

**Key Questions**
- How do political institutions shape economic policy?
- What is the proper role of government in the economy?
- How do economic interests influence political outcomes?
- Why do countries choose different economic systems?

**Levels of Analysis**
- **Individual**: How economic conditions affect voting and political behavior
- **Domestic**: How national institutions and politics shape economic policy
- **International**: How global economic forces affect domestic politics

### Economic Systems and Political Arrangements

**Market Capitalism**
- **Private Property**: Individual ownership of productive resources
- **Market Allocation**: Prices determine resource distribution
- **Competition**: Multiple firms compete for customers
- **Profit Motive**: Firms seek to maximize returns
- **Limited Government**: Minimal state intervention in economy

**Social Market Economy**
- **Mixed System**: Combines market mechanisms with government regulation
- **Social Safety Net**: Government provides welfare and social insurance
- **Industrial Relations**: Cooperation between business and labor
- **Examples**: Germany, Scandinavian countries

**State Capitalism**
- **Government Direction**: State guides economic development
- **Public Ownership**: Government owns key industries
- **Market Elements**: Some private enterprise and market pricing
- **Examples**: China, Singapore, UAE

**Command Economy**
- **Central Planning**: Government makes all economic decisions
- **Public Ownership**: State owns all productive resources
- **Administrative Allocation**: Bureaucrats distribute goods and services
- **Historical Examples**: Soviet Union, Cuba, North Korea

### Government's Role in the Economy

**Market Failure Justifications**

**Public Goods**
- **Non-Excludable**: Cannot prevent people from using
- **Non-Rivalrous**: One person's use doesn't reduce others'
- **Examples**: National defense, lighthouses, basic research
- **Government Solution**: Public provision and taxation

**Natural Monopolies**
- **Economies of Scale**: Single firm most efficient
- **High Fixed Costs**: Difficult for competitors to enter
- **Examples**: Utilities, telecommunications, transportation infrastructure
- **Government Solutions**: Public ownership or regulation

**Externalities**
- **Negative Externalities**: Costs imposed on third parties (pollution)
- **Positive Externalities**: Benefits to third parties (education, R&D)
- **Market Failure**: Private decisions don't account for social costs/benefits
- **Government Solutions**: Taxes, subsidies, regulation, property rights

**Information Asymmetries**
- **Unequal Information**: One party knows more than another
- **Examples**: Insurance markets, financial markets, professional services
- **Problems**: Adverse selection, moral hazard
- **Government Solutions**: Regulation, disclosure requirements, licensing

**Income Distribution**
- **Market Outcomes**: May produce unacceptable inequality
- **Social Concerns**: Poverty, opportunity, social cohesion
- **Government Tools**: Progressive taxation, transfer payments, public services

### Fiscal Policy

**Government Spending**

**Types of Government Expenditures**
- **Discretionary Spending**: Annual appropriations (defense, education)
- **Mandatory Spending**: Entitlement programs (Social Security, Medicare)
- **Interest Payments**: Debt service on government borrowing

**Economic Effects of Spending**
- **Multiplier Effect**: Government spending stimulates additional economic activity
- **Crowding Out**: Government borrowing may reduce private investment
- **Automatic Stabilizers**: Transfer payments increase during recessions

**Taxation**

**Types of Taxes**
- **Income Taxes**: Federal and state taxes on earnings
- **Payroll Taxes**: Social Security and Medicare contributions
- **Corporate Taxes**: Taxes on business profits
- **Sales Taxes**: Taxes on consumption
- **Property Taxes**: Taxes on real estate and assets

**Tax Principles**
- **Equity**: Fair distribution of tax burden
  - **Horizontal Equity**: Equal treatment of equals
  - **Vertical Equity**: Progressive taxation of different income levels
- **Efficiency**: Minimal distortion of economic decisions
- **Simplicity**: Easy to understand and comply with
- **Revenue Adequacy**: Sufficient funds for government operations

**Budget Process and Politics**
- **Budget Formulation**: Executive branch preparation
- **Legislative Review**: Congressional modification and approval
- **Implementation**: Agency spending and program execution
- **Political Constraints**: Interest groups, electoral pressures, partisan conflict

### Monetary Policy

**Central Banking**
- **Federal Reserve System**: US central bank structure
- **Independence**: Insulation from short-term political pressures
- **Dual Mandate**: Price stability and full employment
- **Tools**: Interest rates, money supply, bank regulation

**Monetary Policy Instruments**
- **Federal Funds Rate**: Interest rate on interbank loans
- **Open Market Operations**: Buying and selling government securities
- **Reserve Requirements**: Bank capital and liquidity rules
- **Discount Rate**: Interest rate on Federal Reserve loans to banks

**Political Economy of Monetary Policy**
- **Time Consistency**: Long-term commitments vs. short-term pressures
- **Distributional Effects**: Who benefits from low vs. high interest rates
- **Political Pressure**: Congressional oversight and public criticism
- **International Constraints**: Exchange rates and capital flows

### Regulatory Policy

**Types of Regulation**

**Economic Regulation**
- **Natural Monopolies**: Utilities, telecommunications
- **Financial Markets**: Banking, securities, insurance
- **Transportation**: Airlines, railroads, trucking
- **Goals**: Prevent monopoly pricing, ensure service quality

**Social Regulation**
- **Health and Safety**: Workplace safety, product safety, pharmaceuticals
- **Environmental Protection**: Air and water quality, toxic substances
- **Civil Rights**: Anti-discrimination in employment and housing
- **Goals**: Protect public health, safety, and rights

**Regulatory Process**
- **Rulemaking**: Administrative agencies create detailed regulations
- **Enforcement**: Monitoring compliance and imposing sanctions
- **Adjudication**: Resolving disputes and violations
- **Judicial Review**: Courts review agency decisions

**Regulatory Politics**
- **Capture Theory**: Industries influence their regulators
- **Interest Group Competition**: Multiple groups compete for influence
- **Political Control**: Congressional oversight and presidential direction
- **Bureaucratic Discretion**: Agency expertise and independence

### Trade Policy

**International Trade Theory**
- **Comparative Advantage**: Countries benefit from specialization
- **Gains from Trade**: Consumers benefit from lower prices and variety
- **Adjustment Costs**: Some workers and industries harmed by competition
- **Political Economy**: Concentrated costs vs. diffuse benefits

**Trade Policy Instruments**
- **Tariffs**: Taxes on imported goods
- **Quotas**: Quantitative restrictions on imports
- **Subsidies**: Government support for domestic producers
- **Trade Agreements**: Multilateral and bilateral agreements

**Political Dynamics**
- **Protectionist Pressures**: Import-competing industries seek protection
- **Export Interests**: Export industries favor free trade
- **Consumer Interests**: Diffuse benefits from lower prices
- **Labor vs. Capital**: Different factor interests in trade policy

### Contemporary Policy Challenges

**Globalization and Economic Policy**
- **Capital Mobility**: Constraints on taxation and regulation
- **Trade Competition**: Pressure for competitiveness policies
- **Regulatory Competition**: Race to bottom vs. regulatory innovation
- **Policy Coordination**: Need for international cooperation

**Technology and Disruption**
- **Automation**: Job displacement and skill requirements
- **Digital Economy**: Platform monopolies and data governance
- **Gig Economy**: Labor classification and social protection
- **Innovation Policy**: R&D support and intellectual property

**Inequality and Political Economy**
- **Rising Inequality**: Income and wealth concentration
- **Political Consequences**: Populism and policy backlash
- **Policy Responses**: Progressive taxation, minimum wage, education
- **Political Feasibility**: Coalition building and reform constraints

**Environmental Economics**
- **Market Failures**: Climate change and environmental degradation
- **Policy Instruments**: Carbon taxes, cap-and-trade, regulation
- **Political Economy**: Costs vs. benefits, international coordination
- **Just Transition**: Helping workers and communities affected by change

### Case Studies

**Financial Crisis and Regulation**
- **2008 Financial Crisis**: Causes and political responses
- **Regulatory Reform**: Dodd-Frank Act and banking regulation
- **Political Opposition**: Industry resistance and regulatory rollback
- **International Coordination**: Basel III and global banking standards

**Healthcare Policy**
- **Market Failures**: Insurance markets and healthcare costs
- **Policy Options**: Single payer, insurance regulation, public option
- **Political Dynamics**: Interest groups and partisan conflict
- **Comparative Perspective**: Healthcare systems in other countries

**Climate Policy**
- **Policy Instruments**: Carbon pricing, renewable energy standards
- **Political Challenges**: Concentrated costs, diffuse benefits
- **Interest Group Politics**: Fossil fuel vs. environmental groups
- **International Cooperation**: Paris Agreement and climate diplomacy

Understanding political economy helps explain why countries adopt different economic policies and how political factors shape economic outcomes. The interaction between politics and economics is central to understanding both effective governance and economic performance.`
        },
        {
          id: "policy-tradeoffs",
          title: "Economic Policy Trade-offs Analysis",
          description: "Examine the political and economic trade-offs in major policy decisions",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "policy-tradeoffs",
            instructions: "Analyze major economic policy decisions by examining their political and economic trade-offs, winners and losers, and long-term consequences.",
            policies: [
              {
                name: "Minimum Wage Increase",
                economicEffects: ["Higher wages for workers", "Potential job losses", "Increased business costs", "Reduced poverty"],
                politicalFactors: ["Labor union support", "Business opposition", "Popular public support", "Regional variations"],
                tradeoffs: ["Employment vs. wages", "Small vs. large business impacts", "Regional economic differences"],
                stakeholders: ["Low-wage workers", "Small businesses", "Labor unions", "Consumers"]
              },
              {
                name: "Corporate Tax Reduction",
                economicEffects: ["Increased business investment", "Reduced government revenue", "Potential job creation", "Income distribution effects"],
                politicalFactors: ["Business lobbying", "Revenue concerns", "Partisan positions", "Public opinion"],
                tradeoffs: ["Growth vs. revenue", "Business vs. individual taxpayers", "Short vs. long-term effects"],
                stakeholders: ["Corporations", "Individual taxpayers", "Government programs", "Workers"]
              }
            ],
            analysisFramework: [
              "Identify economic winners and losers",
              "Assess political feasibility and obstacles", 
              "Evaluate short-term vs. long-term effects",
              "Consider alternative policy approaches"
            ],
            hints: [
              "Think about both direct and indirect effects",
              "Consider how concentrated vs. diffuse interests affect politics",
              "Analyze the role of timing and economic conditions"
            ]
          }
        }
      ],
      quiz: {
        id: "political-economy-quiz",
        title: "Political Economy Quiz",
        description: "Test your understanding of the relationship between politics and economics",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary justification for government intervention in cases of natural monopoly?",
            options: [
              "To promote competition",
              "To regulate prices and ensure service quality",
              "To redistribute income",
              "To collect tax revenue"
            ],
            correctAnswer: 1,
            explanation: "In natural monopolies, a single firm is most efficient due to economies of scale, so government regulates prices and service quality rather than promoting competition."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which fiscal policy tool is considered an automatic stabilizer?",
            options: [
              "Defense spending",
              "Infrastructure investment",
              "Unemployment insurance",
              "Interest rate changes"
            ],
            correctAnswer: 2,
            explanation: "Unemployment insurance is an automatic stabilizer because it automatically increases government spending during recessions when more people become unemployed."
          }
        ]
      }
    },

    {
      id: "contemporary-issues",
      title: "Contemporary Political Issues and Challenges",
      description: "Current political debates, challenges facing democratic societies, and emerging issues",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "democratic-challenges",
          title: "Challenges to Democracy",
          description: "Examining threats to democratic governance in the 21st century",
          type: "video",
          duration: 35
        },
        {
          id: "emerging-political-issues",
          title: "Technology, Globalization, and Political Change",
          description: "How technological and global changes are transforming politics",
          type: "text",
          duration: 45,
          textContent: `# Technology, Globalization, and Political Change

## Contemporary Challenges to Democratic Governance

### Democratic Backsliding

**Defining Democratic Backsliding**
Democratic backsliding refers to the weakening of democratic institutions, norms, and practices in countries that were previously considered democratic or democratizing.

**Signs of Democratic Backsliding**
- **Electoral Manipulation**: Gerrymandering, voter suppression, election fraud
- **Media Control**: Government control or influence over news media
- **Judicial Capture**: Political control over courts and legal system
- **Civil Society Restrictions**: Limits on NGOs, protests, and civil liberties
- **Norm Erosion**: Violations of democratic customs and informal rules

**Causes of Democratic Backsliding**
- **Polarization**: Extreme partisan division and us-vs-them mentality
- **Economic Stress**: Economic inequality and insecurity
- **Populist Appeals**: Leaders claiming to represent "the people" against elites
- **Weak Institutions**: Insufficient checks and balances
- **External Interference**: Foreign manipulation and influence campaigns

**Global Examples**
- **Hungary**: Viktor Orbán's consolidation of power
- **Poland**: Judicial reforms and media control
- **Turkey**: Erdoğan's response to coup attempt
- **Venezuela**: Maduro's authoritarian consolidation
- **United States**: Concerns about electoral integrity and polarization

### Populism and Political Polarization

**Populism Defined**
- **People vs. Elite**: Division between ordinary people and corrupt establishment
- **Popular Sovereignty**: Direct expression of popular will
- **Anti-Pluralism**: Claim to represent the "real" people
- **Charismatic Leadership**: Strong personal connection between leader and followers

**Types of Populism**
- **Left-Wing Populism**: Focus on economic inequality and corporate power
- **Right-Wing Populism**: Emphasis on cultural identity and immigration
- **Regional Variations**: Different forms in different contexts

**Political Polarization**
- **Affective Polarization**: Emotional dislike of opposing party
- **Issue Polarization**: Growing disagreement on policy issues
- **Geographic Polarization**: Spatial concentration of partisans
- **Media Polarization**: Separate information ecosystems

**Consequences for Democracy**
- **Reduced Compromise**: Difficulty finding bipartisan solutions
- **Institutional Conflict**: Battles over rules and procedures
- **Democratic Legitimacy**: Questions about election results and fairness
- **Political Violence**: Increased threats and actual violence

### Technology and Digital Transformation

**Digital Revolution's Political Impact**

**Information and Media**
- **Social Media**: Facebook, Twitter, TikTok as news sources
- **Filter Bubbles**: Algorithm-driven content that reinforces beliefs
- **Echo Chambers**: Interaction mainly with like-minded individuals
- **Misinformation**: False information spreading rapidly online
- **Deepfakes**: AI-generated fake audio and video content

**Political Communication**
- **Direct Communication**: Politicians bypass traditional media
- **Micro-Targeting**: Precise advertising to specific groups
- **Viral Campaigns**: Grassroots mobilization through social networks
- **Astroturfing**: Fake grassroots campaigns using bots and fake accounts
- **Data Analytics**: Big data used for voter targeting and persuasion

**Challenges to Democratic Discourse**
- **Information Overload**: Difficulty processing vast amounts of information
- **Source Credibility**: Distinguishing reliable from unreliable sources
- **Fact-Checking**: Efforts to combat misinformation
- **Platform Responsibility**: Social media companies' content moderation
- **Government Regulation**: Balancing free speech and harmful content

### Cybersecurity and Digital Governance

**Cyber Threats to Democracy**
- **Election Security**: Hacking voting systems and election infrastructure
- **Foreign Interference**: Russia, China, Iran election manipulation
- **Data Breaches**: Theft of personal and political information
- **Ransomware**: Attacks on government systems and infrastructure
- **Disinformation Campaigns**: Foreign-sponsored false information

**Digital Rights and Privacy**
- **Surveillance**: Government monitoring of digital communications
- **Data Protection**: GDPR, CCPA, and privacy regulations
- **Encryption**: Balancing security and law enforcement access
- **Platform Power**: Tech company control over information flow
- **Digital Divide**: Unequal access to technology and internet

**Governance Challenges**
- **Jurisdiction**: Applying national law to global platforms
- **Speed of Change**: Regulation lagging behind technological development
- **Technical Expertise**: Policymaker understanding of complex technologies
- **International Cooperation**: Need for global coordination on cyber issues

### Globalization and National Sovereignty

**Economic Globalization**
- **Trade Integration**: Increased international trade and investment
- **Supply Chains**: Global production and distribution networks
- **Capital Flows**: International movement of money and investment
- **Multinational Corporations**: Companies operating across borders
- **Economic Interdependence**: Countries dependent on global economy

**Political Consequences of Globalization**
- **Policy Constraints**: Limited ability to pursue independent policies
- **Regulatory Competition**: Competition to attract investment and jobs
- **Democratic Deficit**: Important decisions made by unelected bodies
- **Popular Backlash**: Anti-globalization movements and nationalism
- **Inequality**: Globalization benefits some groups more than others

**Migration and Border Politics**
- **Economic Migration**: Movement for better economic opportunities
- **Refugee Crisis**: Forced migration due to conflict and persecution
- **Border Security**: Efforts to control international movement
- **Integration Challenges**: Incorporating immigrants into society
- **Political Backlash**: Anti-immigration parties and policies

**Global Governance**
- **Multilateral Institutions**: UN, WTO, IMF role in governance
- **Treaty Obligations**: International agreements limiting sovereignty
- **Supranational Organization**: EU as model of shared sovereignty
- **Global Civil Society**: Transnational NGOs and advocacy networks

### Climate Change and Environmental Politics

**Climate as Political Issue**
- **Scientific Consensus**: Overwhelming agreement on human-caused climate change
- **Political Polarization**: Partisan divisions on climate action
- **Economic Interests**: Fossil fuel industry opposition to regulation
- **Intergenerational Justice**: Current costs for future benefits
- **Global Coordination**: Need for international cooperation

**Policy Responses**
- **Carbon Pricing**: Taxes and cap-and-trade systems
- **Renewable Energy**: Subsidies and mandates for clean energy
- **Regulation**: Environmental standards and limits
- **Green New Deal**: Comprehensive climate and economic policy
- **International Agreements**: Paris Agreement and climate diplomacy

**Political Challenges**
- **Collective Action**: Free rider problems in global cooperation
- **Time Horizons**: Short political cycles vs. long-term problems
- **Distributional Effects**: Who bears costs and receives benefits
- **Economic Transition**: Managing change in affected industries
- **Public Opinion**: Building support for costly policies

### Identity Politics and Social Movements

**Identity-Based Politics**
- **Group Identity**: Politics based on race, gender, religion, sexuality
- **Recognition**: Demands for acknowledgment and respect
- **Representation**: Inclusion in political institutions and processes
- **Intersectionality**: Multiple, overlapping identities and disadvantages

**Contemporary Social Movements**
- **Black Lives Matter**: Racial justice and police reform
- **#MeToo**: Gender equality and sexual harassment
- **LGBTQ+ Rights**: Marriage equality and anti-discrimination
- **Climate Activism**: Environmental justice and youth movements
- **Indigenous Rights**: Land rights and sovereignty claims

**Movement Strategies**
- **Protest and Demonstration**: Public expression of grievances
- **Digital Activism**: Online organizing and mobilization
- **Electoral Politics**: Running candidates and supporting allies
- **Legal Strategies**: Using courts to advance rights
- **Cultural Change**: Shifting norms and attitudes

**Backlash and Counter-Movements**
- **Cultural Conservatism**: Resistance to social change
- **White Identity Politics**: Reaction to minority rights movements
- **Religious Freedom**: Conflicts over accommodation and equality
- **Political Correctness Debates**: Disputes over speech and expression

### Economic Inequality and Political Consequences

**Rising Inequality**
- **Income Inequality**: Growing gap between rich and poor
- **Wealth Concentration**: Assets concentrated among top earners
- **Intergenerational Mobility**: Reduced opportunity for advancement
- **Geographic Inequality**: Differences between regions and communities

**Political Effects of Inequality**
- **Unequal Representation**: Wealthy have more political influence
- **Policy Bias**: Policies favor high-income groups
- **Political Participation**: Lower turnout among disadvantaged groups
- **System Legitimacy**: Questions about fairness of political system
- **Populist Appeal**: Appeals to economic grievances

**Policy Responses**
- **Progressive Taxation**: Higher tax rates on wealthy
- **Minimum Wage**: Increased wages for low-income workers
- **Social Programs**: Expanded safety net and public services
- **Education Policy**: Investments in human capital
- **Labor Rights**: Strengthening unions and worker protections

### Future Challenges and Opportunities

**Emerging Technologies**
- **Artificial Intelligence**: AI impact on jobs and decision-making
- **Biotechnology**: Genetic engineering and human enhancement
- **Space Technology**: Commercial space and resource competition
- **Quantum Computing**: Implications for cybersecurity and computing

**Generational Change**
- **Digital Natives**: Generation raised with internet and social media
- **Changing Values**: Attitudes toward work, family, and society
- **Political Engagement**: New forms of political participation
- **Policy Priorities**: Different issue priorities than older generations

**Institutional Adaptation**
- **Constitutional Reform**: Updating founding documents and structures
- **Electoral Reform**: Changes to voting systems and representation
- **Government Innovation**: Digital government and service delivery
- **International Cooperation**: New forms of global governance

**Opportunities for Renewal**
- **Civic Education**: Teaching democratic values and skills
- **Institutional Reform**: Strengthening democratic institutions
- **Civil Society**: Supporting independent organizations and media
- **Technology Solutions**: Using technology to enhance democracy
- **Youth Engagement**: Involving young people in political process

Understanding contemporary challenges helps identify both threats to democratic governance and opportunities for renewal and improvement. The key is adapting democratic institutions and practices to meet new challenges while preserving core democratic values and principles.`
        },
        {
          id: "future-democracy",
          title: "Future of Democracy Debate",
          description: "Structured debate on the future challenges and opportunities for democratic governance",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "structured-debate",
            instructions: "Participate in a structured debate about the future of democratic governance, taking different perspectives on key challenges and solutions.",
            topic: "Is democracy in crisis, and what can be done to strengthen democratic institutions?",
            positions: [
              {
                stance: "Democracy in Crisis",
                arguments: ["Rising authoritarianism globally", "Increasing polarization and partisan conflict", "Misinformation and media fragmentation", "Economic inequality undermining political equality"],
                evidence: ["Freedom House democracy ratings decline", "Voter suppression efforts", "Social media manipulation", "Wealth concentration statistics"]
              },
              {
                stance: "Democracy Adapting",
                arguments: ["Democratic institutions showing resilience", "New forms of political participation emerging", "Technology creating opportunities for engagement", "Civil society and youth activism growing"],
                evidence: ["Election integrity holding despite challenges", "Digital organizing and mobilization", "Fact-checking and media literacy efforts", "Protest movements and civic engagement"]
              },
              {
                stance: "Reform Solutions",
                arguments: ["Institutional reforms needed", "Campaign finance reform essential", "Civic education must be strengthened", "International cooperation required"],
                evidence: ["Ranked choice voting successes", "Public campaign financing experiments", "Civic education curriculum development", "Democratic assistance programs"]
              }
            ],
            debateStructure: [
              "Opening statements (2 minutes each side)",
              "Cross-examination (3 minutes each side)",
              "Rebuttal phase (2 minutes each side)",
              "Closing arguments (2 minutes each side)"
            ],
            hints: [
              "Use specific examples and evidence to support your arguments",
              "Address counterarguments and show understanding of other perspectives",
              "Connect current events to broader theoretical concepts"
            ]
          }
        }
      ],
      quiz: {
        id: "contemporary-issues-quiz",
        title: "Contemporary Political Issues Quiz",
        description: "Test your knowledge of current political challenges and trends",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which of the following is NOT typically considered a sign of democratic backsliding?",
            options: [
              "Electoral manipulation and voter suppression",
              "Government control over media outlets",
              "Peaceful transfer of power after elections",
              "Restrictions on civil society organizations"
            ],
            correctAnswer: 2,
            explanation: "Peaceful transfer of power after elections is actually a sign of healthy democratic institutions, not democratic backsliding."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the main characteristic that distinguishes populist movements from other political movements?",
            options: [
              "They focus only on economic issues",
              "They claim to represent 'the people' against corrupt elites",
              "They always advocate for smaller government",
              "They reject all forms of international cooperation"
            ],
            correctAnswer: 1,
            explanation: "The defining characteristic of populism is the claim to represent 'the real people' against a corrupt establishment or elite class."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "policy-analysis-project",
      title: "Comparative Policy Analysis Project",
      description: "Analyze how different political systems address a specific policy challenge",
      type: "project",
      dueWeeks: 10,
      maxScore: 100,
      instructions: "Choose a policy area (healthcare, education, environment) and compare how different countries with different political systems approach the issue. Analyze the role of institutions in shaping policy outcomes.",
      submissionFormat: "pdf",
      resources: [
        "Comparative government databases",
        "Policy analysis frameworks",
        "Academic political science journals",
        "Government documents and reports"
      ]
    }
  ],

  finalExam: {
    id: "political-science-final-exam",
    title: "Introduction to Political Science Final Examination",
    description: "Comprehensive examination covering political theory, comparative government, and contemporary political issues",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which type of electoral system is most likely to produce a multi-party system?",
        options: [
          "First-past-the-post",
          "Two-round system",
          "Proportional representation",
          "Electoral college"
        ],
        correctAnswer: 2,
        explanation: "Proportional representation electoral systems tend to produce multi-party systems because they allow smaller parties to win seats proportional to their vote share."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "According to Max Weber, what are the three types of authority?",
        options: [
          "Executive, legislative, judicial",
          "Traditional, charismatic, legal-rational",
          "Federal, state, local",
          "Democratic, authoritarian, totalitarian"
        ],
        correctAnswer: 1,
        explanation: "Max Weber identified three types of legitimate authority: traditional (based on custom), charismatic (based on personal qualities), and legal-rational (based on rules and procedures)."
      }
    ]
  },

  quizzes: []
};